<template>
  <VField :name="name">
    <div
      :class="cn(`${props.variant}-input`, props.class)"
      class="flex flex-row items-center gap-2"
      :style="{
        '--size': props.size,
      }"
    >
      <div :class="cn(`${props.variant}-input-trigger`, props.checkboxClass)">
        <input
          type="checkbox"
          :id="props.id ?? props.name"
          :name="props.name"
          :checked="props.checked"
          @input="handleChange"
          @change="handleChange"
          class="absolute opacity-0 w-0 h-0"
        />
        <label class="opacity-0 absolute inset-0 z-1" :for="props.id ?? props.name" />
        <CheckIcon class="transition-opacity z-0 opacity-0" :class="cn(`${props.variant}-input-trigger-icon`)" />
      </div>
      <label
        class="text-sm font-medium"
        :class="cn(`${props.variant}-input-label`)"
        :for="props.id ?? props.name"
        v-if="hasSlot"
      >
        <slot></slot>
      </label>
    </div>
    <VErrorMessage
      :name="props.name"
      as="div"
      class="md:mt-1 mb-2 -mt-3 text-xs text-red-500 px-2 pointer-events-none"
    />
  </VField>
</template>

<script setup lang="ts">
import { CheckIcon } from "@heroicons/vue/outline";
import { type FormContext } from "vee-validate";

interface CheckboxInputEmits {
  (e: "change", state: boolean): void;
}
const emits = defineEmits<CheckboxInputEmits>();

interface CheckboxInputProps {
  name: string;
  id?: string;
  size?: number;
  class?: string;
  checkboxClass?: string;
  form?: FormContext;
  variant?: "checkbox" | "switch";
  checked?: boolean;
}

const props = withDefaults(defineProps<CheckboxInputProps>(), {
  checked: false,
  size: 1,
  variant: "checkbox",
});

const slots = useSlots();
const hasSlot = computed(() => !!slots["default"]);

function handleChange(e: Event) {
  const target = e.target as HTMLInputElement;
  props.form?.setFieldValue(props.name, target.checked, true);
  emits("change", target.checked);
}
</script>

<style scoped lang="scss">
.checkbox-input {
  &:has(input:checked) &-trigger {
    @apply text-primary;

    &-icon {
      @apply opacity-100;
    }
  }

  &-trigger {
    @apply relative w-[1em] h-[1em] shrink-0 border border-current rounded-[4px] text-secondary transition-colors;
    font-size: calc(1em * var(--size, 1));
  }
}

.switch-input {
  &:has(input:checked) &-trigger {
    @apply text-primary;

    &::after {
      @apply translate-x-full;
    }
  }

  &-trigger {
    @apply relative w-[2em] h-[1em] shrink-0 rounded-full bg-current text-slate-300 transition-all;
    font-size: calc(1em * var(--size, 1));

    &::after {
      content: "";
      @apply bg-white rounded-full aspect-square h-[1em] left-0 border border-current absolute top-1/2 -translate-y-1/2 transition-[inherit];
    }
  }
}
</style>
